/**
 * Policy for opening a chat from the home / workspace-detail surfaces.
 *
 * When the floating chat surface is active on the current route + viewport, a
 * chat opens in the floating window; otherwise it falls back to today's
 * behavior — a full-route push to `/chat/:flowSessionId`, which `ChatTabSync`
 * turns into an in-app tab. Every entry point (browser rows, "+ New" menus, the
 * left rail) routes through this one seam so the floating-vs-tab decision lives
 * in a single place and degrades gracefully outside the provider.
 */

import { ROUTES } from "@/constants/routes";
import {
  useIsFloatingChatSurfaceActive,
  useWorkspaceFloatingChatOptional,
  type WorkspaceFloatingSessionHistory,
} from "@/context/WorkspaceFloatingChatContext";
import * as analytics from "@/utils/analytics";
import { route } from "@/utils/route";
import { useCallback } from "react";
import { useMatch, useNavigate } from "react-router-dom";

/** Where an open originated — carried into analytics only. */
export type OpenChatSource =
  | "hero-composer"
  | "browser-row"
  | "new-menu"
  | "rail"
  | "rail-selection";

export interface OpenChatOptions {
  /** Stack semantics when opening floating. Fresh threads use "replace". */
  mode?: WorkspaceFloatingSessionHistory;
  source?: OpenChatSource;
}

export interface UseOpenChatResult {
  /** True when `openChat` will surface the floating window rather than navigate. */
  canOpenFloating: boolean;
  openChat: (flowSessionId: string, opts?: OpenChatOptions) => void;
}

export function useOpenChat(): UseOpenChatResult {
  const navigate = useNavigate();
  const floatingChat = useWorkspaceFloatingChatOptional();
  const surfaceActive = useIsFloatingChatSurfaceActive();
  const isWorkspaceDetail = !!useMatch(ROUTES.WORKSPACES.DETAIL);

  const canOpenFloating = surfaceActive && !!floatingChat;

  const openChat = useCallback(
    (flowSessionId: string, opts?: OpenChatOptions) => {
      if (!flowSessionId) return;
      if (canOpenFloating && floatingChat) {
        floatingChat.openSession(flowSessionId, opts?.mode ?? "push");
        analytics.trackFloatingChatOpened({
          source: opts?.source ?? "browser-row",
          workspaceScoped: isWorkspaceDetail,
        });
        return;
      }
      navigate(route(ROUTES.CHAT.DETAIL, { flowSessionId }));
    },
    [canOpenFloating, floatingChat, navigate, isWorkspaceDetail],
  );

  return { canOpenFloating, openChat };
}
